/**
 * Shared CSV export helpers (PBI 55106, Bug 56846).
 *
 * The filename rule from AC5 lives here and nowhere else — it was previously
 * copy-pasted into each page's `exportCsv()` and drifted out of spec in all of them.
 */

/** Quote a cell per RFC 4180 when it contains a comma, quote, or line break (AC6). */
export function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Lowercase kebab-case form of a release name, for use in a filename (AC5).
 * "Spring Refresh" -> "spring-refresh". Falls back to "release" when empty.
 */
export function kebabCase(value: string | null | undefined): string {
  const slug = (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'release';
}

/**
 * Today's date as YYYY-MM-DD (AC5). Built from local date parts rather than
 * `toISOString()`, which reports the previous day for evening exports in
 * timezones behind UTC.
 */
export function todayStamp(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Serialize rows to RFC 4180 CSV text. The first row is the header row (AC4). */
export function toCsv(rows: string[][]): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
}

/**
 * Build the CSV and hand it to the browser as a download named
 * `[release-name]-[suffix]-[YYYY-MM-DD].csv` (AC2, AC5).
 */
export function downloadCsv(rows: string[][], releaseName: string | null | undefined, suffix: string): void {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${kebabCase(releaseName)}-${suffix}-${todayStamp()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
